"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Metadata = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const internal_1 = require("../../../../../internal");
const types_1 = require("../../../../../types");
const conversion_1 = require("../../../../../utils/conversion");
const internal_2 = require("../../../../../utils/internal");
/**
 * Handles all Asset Metadata related functionality
 */
class Metadata extends internal_1.Namespace {
    /**
     * @hidden
     */
    constructor(parent, context) {
        super(parent, context);
        this.register = (0, internal_2.createProcedureMethod)({ getProcedureAndArgs: args => [internal_1.registerMetadata, Object.assign({ asset: parent }, args)] }, context);
    }
    /**
     * Retrieve all (global + local) the MetadataEntry for this Asset
     *
     * @note this returns all available metadata entries for this Asset, with or without any value being associated with the metadata
     */
    get() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset: { assetMetadataLocalKeyToName, assetMetadataGlobalKeyToName }, }, }, }, context, parent, } = this;
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(parent, context);
            const [rawGlobalKeys, rawLocalKeys] = yield Promise.all([
                assetMetadataGlobalKeyToName.entries(),
                assetMetadataLocalKeyToName.entries(rawAssetId),
            ]);
            const assembleResult = (rawId, type) => new internal_1.MetadataEntry({ assetId: parent.id, type, id: (0, conversion_1.u64ToBigNumber)(rawId) }, context);
            return [
                ...rawGlobalKeys.map(([{ args: [rawId], },]) => assembleResult(rawId, types_1.MetadataType.Global)),
                ...rawLocalKeys.map(([{ args: [, rawId], },]) => assembleResult(rawId, types_1.MetadataType.Local)),
            ];
        });
    }
    /**
     * Retrieve a single MetadataEntry by its ID and type
     *
     * @throws if there is no MetadataEntry with the passed ID and specified type
     */
    getOne(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset: { assetMetadataLocalKeyToName, assetMetadataGlobalKeyToName }, }, }, }, context, parent, } = this;
            const { id, type } = args;
            const rawId = (0, conversion_1.bigNumberToU64)(id, context);
            let rawName;
            if (type === types_1.MetadataType.Global) {
                rawName = yield assetMetadataGlobalKeyToName(rawId);
            }
            else {
                const rawAssetId = (0, conversion_1.assetToMeshAssetId)(parent, context);
                rawName = yield assetMetadataLocalKeyToName(rawAssetId, rawId);
            }
            if (rawName.isEmpty) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.DataUnavailable,
                    message: `There is no ${type.toLowerCase()} Asset Metadata with id "${id.toString()}"`,
                });
            }
            return new internal_1.MetadataEntry({ assetId: parent.id, type, id }, context);
        });
    }
    /**
     * Gets the next local metadata ID for the Asset
     *
     * @hidden
     */
    getNextLocalId() {
        return __awaiter(this, void 0, void 0, function* () {
            const { parent: asset, context, context: { polymeshApi: { query: { asset: { currentAssetMetadataLocalKey }, }, }, }, } = this;
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(asset, context);
            const rawId = yield currentAssetMetadataLocalKey(rawAssetId);
            if (rawId.isSome) {
                return (0, conversion_1.u64ToBigNumber)(rawId.unwrap()).plus(1);
            }
            return new bignumber_js_1.default(1);
        });
    }
    /**
     * Retrieve all (local + global) the MetadataEntry details whose value is set for this Asset
     */
    getDetails() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset: { assetMetadataValues, assetMetadataValueDetails, assetMetadataLocalKeyToName, assetMetadataGlobalKeyToName, assetMetadataGlobalSpecs, assetMetadataLocalSpecs, }, }, }, }, context, parent, } = this;
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(parent, context);
            const [rawValueEntries] = yield Promise.all([assetMetadataValues.entries(rawAssetId)]);
            const namePromises = [];
            const specPromises = [];
            const valueDetailsPromises = [];
            rawValueEntries.forEach(([{ args: [, rawMetadataKey], },]) => {
                valueDetailsPromises.push(assetMetadataValueDetails(rawAssetId, rawMetadataKey));
                if (rawMetadataKey.isLocal) {
                    namePromises.push(assetMetadataLocalKeyToName(rawAssetId, rawMetadataKey.asLocal));
                    specPromises.push(assetMetadataLocalSpecs(rawAssetId, rawMetadataKey.asLocal));
                }
                else {
                    namePromises.push(assetMetadataGlobalKeyToName(rawMetadataKey.asGlobal));
                    specPromises.push(assetMetadataGlobalSpecs(rawMetadataKey.asGlobal));
                }
            });
            const nameValues = yield Promise.all(namePromises);
            const specValues = yield Promise.all(specPromises);
            const valueDetails = yield Promise.all(valueDetailsPromises);
            const data = [];
            let index = 0;
            for (const rawValueEntry of rawValueEntries) {
                const [{ args: [, rawMetadataKey], }, rawValue,] = rawValueEntry;
                data.push(Object.assign({ metadataEntry: new internal_1.MetadataEntry(Object.assign(Object.assign({}, (yield (0, conversion_1.meshMetadataKeyToMetadataKey)(rawMetadataKey, parent, context))), { assetId: parent.id }), context), name: (0, conversion_1.bytesToString)(nameValues[index].unwrap()), specs: (0, conversion_1.meshMetadataSpecToMetadataSpec)(specValues[index]) }, (0, conversion_1.meshMetadataValueToMetadataValue)(rawValue, valueDetails[index])));
                index++;
            }
            return data;
        });
    }
}
exports.Metadata = Metadata;
//# sourceMappingURL=index.js.map